import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FluentProvider,
  tokens,
  makeStyles,
  Spinner,
  Button,
  Text,
} from '@fluentui/react-components';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from './authConfig';
import { InteractionStatus, AccountInfo } from '@azure/msal-browser';
import { lightTheme, darkTheme } from './theme';
import { Message, Agent, ModelConfig, TokenUsage, Conversation } from './types';
import { HeaderBar } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { ChatWindow } from './components/ChatWindow';
import { SettingsPanel } from './components/SettingsPanel';
import {
  loadAllConversations,
  saveConversation,
  deleteConversation as deleteConversationFromStorage,
  purgeOldConversations,
  generateConversationTitle,
} from './conversationStorage';
import androidChromeIcon from './assets/android-chrome-512x512.png';

// --- Styles ---

const useStyles = makeStyles({
  app: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
  },
  mainContent: {
    display: 'flex',
    flexGrow: 1,
    overflow: 'hidden',
    background: tokens.colorNeutralBackground2,
  },
  globalSpinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  globalSpinnerLogo: {
    height: '80px',
    width: '80px',
    borderRadius: tokens.borderRadiusCircular,
  },
  loginPage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: 'linear-gradient(135deg, #2B3240 0%, #3d4a5c 50%, #2B3240 100%)',
  },
  loginCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalL,
    padding: '48px 40px',
    borderRadius: tokens.borderRadiusXLarge,
    background: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow64,
    maxWidth: '400px',
    width: '90%',
    textAlign: 'center',
  },
  loginLogo: {
    height: '80px',
    width: '80px',
    borderRadius: tokens.borderRadiusCircular,
  },
  loginTitle: {
    fontFamily: '"Agency FB", sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    color: tokens.colorBrandForeground1,
  },
  loginSubtitle: {
    color: tokens.colorNeutralForeground2,
  },
  loginButton: {
    marginTop: tokens.spacingVerticalM,
    minWidth: '220px',
  },
});

// --- Available models hook ---

const DEFAULT_MODELS: ModelConfig[] = [
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini', provider: 'azure-openai', model: 'gpt-4o-mini', description: 'Cost-effective', capabilities: ['chat'], enabled: true },
  { id: 'gpt-4o', name: 'GPT-4o', provider: 'azure-openai', model: 'gpt-4o', description: 'Most capable', capabilities: ['chat', 'vision'], enabled: true },
];

function useAvailableModels() {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetch('/api/models')
      .then(res => res.ok ? res.json() : Promise.reject(res.statusText))
      .then(data => {
        if (!cancelled) {
          const enabled = (data.models || []).filter((m: ModelConfig) => m.enabled);
          setModels(enabled.length > 0 ? enabled : DEFAULT_MODELS);
        }
      })
      .catch(err => {
        console.warn('Failed to fetch models from /api/models, using defaults:', err);
        if (!cancelled) setModels(DEFAULT_MODELS);
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { models, isLoadingModels: isLoading };
}

// --- SSE stream parser ---

function parseSSEChunk(chunk: string): Array<{ content?: string; usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number }; foundryThreadId?: string }> {
  const results: Array<{ content?: string; usage?: any; foundryThreadId?: string }> = [];
  const lines = chunk.split('\n');

  for (const line of lines) {
    if (line.startsWith('data: ')) {
      const data = line.slice(6).trim();
      if (data === '[DONE]') continue;
      try {
        const parsed = JSON.parse(data);
        const delta = parsed.choices?.[0]?.delta;
        const usage = parsed.usage;
        if (delta?.content) {
          results.push({ content: delta.content });
        }
        if (usage) {
          results.push({ usage });
        }
        if (parsed.foundryThreadId) {
          results.push({ foundryThreadId: parsed.foundryThreadId });
        }
      } catch {
        // Skip malformed JSON lines
      }
    }
  }

  return results;
}

// --- App ---

const App: React.FC = () => {
  const styles = useStyles();
  const { accounts, inProgress, instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [activeAgentId, setActiveAgentId] = useState<string | null>(null);

  // Theme
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('paceChat-theme');
    return saved ? saved === 'dark' : false;
  });
  const theme = isDarkMode ? darkTheme : lightTheme;
  const toggleTheme = () => {
    setIsDarkMode(prev => {
      const newMode = !prev;
      localStorage.setItem('paceChat-theme', newMode ? 'dark' : 'light');
      return newMode;
    });
  };

  // Model selection (persisted)
  const { models: availableModels, isLoadingModels } = useAvailableModels();
  const [selectedModel, setSelectedModel] = useState<string>(() => {
    return localStorage.getItem('paceChat-selectedModel') || '';
  });

  useEffect(() => {
    if (availableModels.length > 0 && !selectedModel) {
      const defaultModel = availableModels[0].model;
      setSelectedModel(defaultModel);
      localStorage.setItem('paceChat-selectedModel', defaultModel);
    }
  }, [availableModels, selectedModel]);

  const handleModelChange = useCallback((model: string) => {
    setSelectedModel(model);
    localStorage.setItem('paceChat-selectedModel', model);
  }, []);

  // Session token tracking
  const [sessionTokens, setSessionTokens] = useState<TokenUsage>({ promptTokens: 0, completionTokens: 0, totalTokens: 0 });

  // Agents state
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [agentsError, setAgentsError] = useState<string | null>(null);

  const fetchAgents = useCallback(async () => {
    setIsLoadingAgents(true);
    setAgentsError(null);
    try {
      // Fetch user-created agents and Pace Agents in parallel
      const [userRes, paceRes] = await Promise.all([
        fetch('/api/agents'),
        fetch('/api/pace-agents'),
      ]);
      if (!userRes.ok) throw new Error(await userRes.text());
      const userData = await userRes.json();
      const userAgents: Agent[] = (userData.agents || []).map((a: any) => ({
        ...a,
        avatar: a.avatarURL || undefined,
      }));

      let paceAgents: Agent[] = [];
      if (paceRes.ok) {
        const paceData = await paceRes.json();
        paceAgents = (paceData.agents || []).map((a: any) => ({
          ...a,
          isPaceAgent: true,
        }));
      } else {
        console.warn('Failed to fetch Pace Agents:', await paceRes.text().catch(() => ''));
      }

      setAgents([...userAgents, ...paceAgents]);
    } catch (err: any) {
      console.error('Failed to fetch agents:', err);
      setAgentsError(`Failed to load agents: ${err.message}`);
      setAgents([]);
    } finally {
      setIsLoadingAgents(false);
    }
  }, []);

  // Profile photo (lifted from Header for shared use)
  const [profilePhoto, setProfilePhoto] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchProfilePhoto = async () => {
      if (!isAuthenticated || !account) { setProfilePhoto(undefined); return; }
      try {
        const response = await instance.acquireTokenSilent({ ...loginRequest, account });
        const photoRes = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
          headers: { Authorization: `Bearer ${response.accessToken}` },
        });
        if (photoRes.ok) {
          const blob = await photoRes.blob();
          const reader = new FileReader();
          reader.onloadend = () => setProfilePhoto(reader.result as string);
          reader.readAsDataURL(blob);
        }
      } catch (e) {
        console.error('Profile photo fetch failed:', e);
      }
    };
    fetchProfilePhoto();
  }, [isAuthenticated, account, instance]);

  // Layout
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState("60px");

  // Chat state
  const [messages, setMessages] = useState<Message[]>([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const messagesRef = useRef<Message[]>([]);
  messagesRef.current = messages;

  // AbortController for stop generation
  const abortControllerRef = useRef<AbortController | null>(null);

  // Streaming speed
  const [streamingSpeed, setStreamingSpeed] = useState<number>(0);
  const streamStartRef = useRef<number>(0);
  const streamCharsRef = useRef<number>(0);

  // Conversation state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const convCreatedAtRef = useRef<string>('');
  const skipNextAgentResetRef = useRef(false);
  // Tracks the Foundry thread ID for the active conversation (Pace Agents only)
  const activeConvThreadIdRef = useRef<string | null>(null);

  // Auth setup
  useEffect(() => {
    if (accounts.length > 0) {
      setAccount(accounts[0]);
    } else {
      instance.ssoSilent(loginRequest)
        .then(response => setAccount(response.account))
        .catch(() => setAccount(null));
    }
  }, [accounts, instance]);

  // Fetch agents once authenticated
  useEffect(() => {
    if (account && inProgress === InteractionStatus.None) {
      fetchAgents();
    }
  }, [account, inProgress, fetchAgents]);

  // Load conversations on mount
  useEffect(() => {
    purgeOldConversations();
    setConversations(loadAllConversations());
  }, []);

  // Reset chat state when agent changes (unless loading a conversation)
  useEffect(() => {
    if (skipNextAgentResetRef.current) {
      skipNextAgentResetRef.current = false;
      return;
    }
    setMessages([]);
    setChatError(null);
    setIsSendingMessage(false);
    setSessionTokens({ promptTokens: 0, completionTokens: 0, totalTokens: 0 });
    setActiveConversationId(null);
    setStreamingSpeed(0);
    activeConvThreadIdRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAgentId]);

  // Stop generation
  const stopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsSendingMessage(false);
    setStreamingSpeed(0);
  }, []);

  // Clear conversation (new chat)
  const clearConversation = useCallback(() => {
    setMessages([]);
    setChatError(null);
    setSessionTokens({ promptTokens: 0, completionTokens: 0, totalTokens: 0 });
    setActiveConversationId(null);
    setStreamingSpeed(0);
    activeConvThreadIdRef.current = null;
  }, []);

  // Send message
  const sendMessage = useCallback(async (messageContent: string, overrideMessages?: Message[]) => {
    if (!activeAgentId || !messageContent.trim()) return;
    setIsSendingMessage(true);
    setChatError(null);
    setStreamingSpeed(0);

    const agent = agents.find(a => a.id === activeAgentId);
    const messagesToUse = overrideMessages || messagesRef.current;

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageContent,
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);

    // Ensure conversation exists
    let convId = activeConversationId;
    if (!convId) {
      convId = `conv-${Date.now()}`;
      convCreatedAtRef.current = new Date().toISOString();
      setActiveConversationId(convId);
    }

    const assistantMsgId = `assistant-${Date.now()}`;
    abortControllerRef.current = new AbortController();

    // --- Shared SSE streaming handler ---
    const handleSSEStream = async (response: Response) => {
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let usage: TokenUsage | undefined;

      setMessages(prev => [...prev, {
        id: assistantMsgId,
        role: 'assistant',
        content: '',
        createdAt: new Date().toISOString(),
      }]);

      streamStartRef.current = Date.now();
      streamCharsRef.current = 0;

      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
          const events = parseSSEChunk(part);
          for (const event of events) {
            if (event.content) {
              fullContent += event.content;
              streamCharsRef.current += event.content.length;
              const elapsed = (Date.now() - streamStartRef.current) / 1000;
              if (elapsed > 0.5) {
                setStreamingSpeed(Math.round(streamCharsRef.current / 4 / elapsed));
              }
              const updatedContent = fullContent;
              setMessages(prev => prev.map(m =>
                m.id === assistantMsgId ? { ...m, content: updatedContent } : m
              ));
            }
            if (event.usage) {
              usage = {
                promptTokens: event.usage.prompt_tokens || 0,
                completionTokens: event.usage.completion_tokens || 0,
                totalTokens: event.usage.total_tokens || 0,
              };
            }
            if (event.foundryThreadId) {
              activeConvThreadIdRef.current = event.foundryThreadId;
            }
          }
        }
      }

      // Process remaining buffer
      if (buffer.trim()) {
        const events = parseSSEChunk(buffer);
        for (const event of events) {
          if (event.content) {
            fullContent += event.content;
            const updatedContent = fullContent;
            setMessages(prev => prev.map(m =>
              m.id === assistantMsgId ? { ...m, content: updatedContent } : m
            ));
          }
          if (event.usage) {
            usage = {
              promptTokens: event.usage.prompt_tokens || 0,
              completionTokens: event.usage.completion_tokens || 0,
              totalTokens: event.usage.total_tokens || 0,
            };
          }
          if (event.foundryThreadId) {
            activeConvThreadIdRef.current = event.foundryThreadId;
          }
        }
      }

      if (usage) {
        setMessages(prev => prev.map(m =>
          m.id === assistantMsgId ? { ...m, tokenUsage: usage } : m
        ));
        setSessionTokens(prev => ({
          promptTokens: prev.promptTokens + usage!.promptTokens,
          completionTokens: prev.completionTokens + usage!.completionTokens,
          totalTokens: prev.totalTokens + usage!.totalTokens,
        }));
      }
    };

    try {
      const isPaceAgent = agent?.isPaceAgent && agent?.foundryAgentId;

      if (isPaceAgent) {
        // --- Pace Agent (Azure AI Foundry) path ---
        const response = await fetch('/api/foundry-chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            foundryAgentId: agent!.foundryAgentId,
            foundryEndpoint: agent!.foundryEndpoint || undefined,
            threadId: activeConvThreadIdRef.current || undefined,
            message: messageContent,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          let errorDetail = `HTTP ${response.status}`;
          try {
            const errJson = await response.json();
            errorDetail = errJson.error || JSON.stringify(errJson);
          } catch {
            errorDetail = await response.text().catch(() => errorDetail);
          }
          throw new Error(errorDetail);
        }

        const contentType = response.headers.get('Content-Type') || '';
        if (contentType.includes('text/event-stream') && response.body) {
          await handleSSEStream(response);
        }
      } else {
        // --- Regular agent (Azure OpenAI chat completions) path ---
        const openAIMessages = [
          ...(agent?.instructions ? [{ role: 'system' as const, content: agent.instructions }] : []),
          ...messagesToUse.map(m => ({ role: m.role as string, content: m.content })),
          { role: 'user', content: messageContent },
        ];

        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ modelId: selectedModel, messages: openAIMessages }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          let errorDetail = `HTTP ${response.status}`;
          try {
            const errJson = await response.json();
            console.error('[Chat] API error:', errJson);
            errorDetail = errJson.error || JSON.stringify(errJson);
            if (errJson.debug) console.error('[Chat] Debug info:', errJson.debug);
          } catch {
            errorDetail = await response.text().catch(() => errorDetail);
          }
          throw new Error(errorDetail);
        }

        const contentType = response.headers.get('Content-Type') || '';

        if (contentType.includes('text/event-stream') && response.body) {
          await handleSSEStream(response);
        } else {
          // Non-streaming JSON response (fallback)
          const data = await response.json();
          const assistantContent = data.content || data.choices?.[0]?.message?.content || '';
          const assistantMsg: Message = {
            id: assistantMsgId,
            role: 'assistant',
            content: assistantContent,
            createdAt: new Date().toISOString(),
          };

          if (data.usage) {
            assistantMsg.tokenUsage = {
              promptTokens: data.usage.prompt_tokens || 0,
              completionTokens: data.usage.completion_tokens || 0,
              totalTokens: data.usage.total_tokens || 0,
            };
            setSessionTokens(prev => ({
              promptTokens: prev.promptTokens + (data.usage.prompt_tokens || 0),
              completionTokens: prev.completionTokens + (data.usage.completion_tokens || 0),
              totalTokens: prev.totalTokens + (data.usage.total_tokens || 0),
            }));
          }

          setMessages(prev => [...prev, assistantMsg]);
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return; // User cancelled
      setChatError(`Failed to send message: ${err.message}`);
    } finally {
      setIsSendingMessage(false);
      abortControllerRef.current = null;
      setStreamingSpeed(0);
    }
  }, [activeAgentId, agents, selectedModel, activeConversationId]);

  // Auto-save conversation when sending completes
  const prevSendingRef = useRef(false);
  useEffect(() => {
    if (prevSendingRef.current && !isSendingMessage && activeConversationId && messages.length > 0 && activeAgentId) {
      const firstUserMsg = messages.find(m => m.role === 'user');
      saveConversation({
        id: activeConversationId,
        agentId: activeAgentId,
        title: firstUserMsg ? generateConversationTitle(firstUserMsg.content) : 'New Chat',
        messages,
        modelId: selectedModel,
        createdAt: convCreatedAtRef.current || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        threadId: activeConvThreadIdRef.current || undefined,
      });
      setConversations(loadAllConversations());
    }
    prevSendingRef.current = isSendingMessage;
  }, [isSendingMessage, messages, activeConversationId, activeAgentId, selectedModel]);

  // Regenerate last response
  const regenerateLastResponse = useCallback(async () => {
    const currentMessages = messagesRef.current;
    const lastUserMsg = [...currentMessages].reverse().find(m => m.role === 'user');
    if (!lastUserMsg) return;

    const lastAssistantIdx = currentMessages.map((m, i) => ({ m, i })).filter(x => x.m.role === 'assistant').pop()?.i;
    if (lastAssistantIdx === undefined) return;

    const truncated = currentMessages.slice(0, lastAssistantIdx);
    setMessages(truncated);
    await sendMessage(lastUserMsg.content, truncated);
  }, [sendMessage]);

  // Edit and resend
  const editAndResend = useCallback(async (messageId: string, newContent: string) => {
    const currentMessages = messagesRef.current;
    const msgIndex = currentMessages.findIndex(m => m.id === messageId);
    if (msgIndex === -1) return;

    const truncated = currentMessages.slice(0, msgIndex);
    setMessages(truncated);
    await sendMessage(newContent, truncated);
  }, [sendMessage]);

  // Load conversation
  const loadConversation = useCallback((convId: string) => {
    const conv = conversations.find(c => c.id === convId);
    if (!conv) return;

    // Stop any in-progress generation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsSendingMessage(false);

    if (conv.agentId !== activeAgentId) {
      skipNextAgentResetRef.current = true;
      setActiveAgentId(conv.agentId);
    }

    setActiveConversationId(convId);
    convCreatedAtRef.current = conv.createdAt;
    activeConvThreadIdRef.current = conv.threadId || null;
    setMessages(conv.messages);
    setChatError(null);
    setSessionTokens({ promptTokens: 0, completionTokens: 0, totalTokens: 0 });
    setStreamingSpeed(0);
  }, [conversations, activeAgentId]);

  // Delete conversation
  const handleDeleteConversation = useCallback((convId: string) => {
    deleteConversationFromStorage(convId);
    setConversations(loadAllConversations());
    if (activeConversationId === convId) {
      clearConversation();
    }
  }, [activeConversationId, clearConversation]);

  const handleSelectAgent = useCallback((agentId: string | null) => {
    setActiveAgentId(agentId);
    if (isMobileView && isMobileSidebarOpen) setIsMobileSidebarOpen(false);
  }, [isMobileView, isMobileSidebarOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        clearConversation();
      }
      if (e.key === 'Escape' && isSendingMessage) {
        stopGeneration();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clearConversation, isSendingMessage, stopGeneration]);

  // Responsive
  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobileView(mobile);
      if (!mobile) setIsMobileSidebarOpen(false);
    };
    window.addEventListener('resize', check);
    check();
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (headerRef.current) setHeaderHeight(`${headerRef.current.offsetHeight}px`);
  }, [account, isMobileView, inProgress]);

  const handleLogin = useCallback(() => {
    instance.loginPopup(loginRequest).catch(e => console.error("Login failed:", e));
  }, [instance]);

  // Auth loading
  if (
    inProgress === InteractionStatus.Startup ||
    inProgress === InteractionStatus.HandleRedirect ||
    (inProgress === InteractionStatus.SsoSilent && !isAuthenticated)
  ) {
    return (
      <FluentProvider theme={theme}>
        <div className={styles.globalSpinnerContainer}>
          <img src={androidChromeIcon} alt="Pace AI Logo" className={styles.globalSpinnerLogo} />
          <Spinner label="Signing you in..." size="large" />
        </div>
      </FluentProvider>
    );
  }

  // Login gate
  if (!isAuthenticated || !account) {
    return (
      <FluentProvider theme={theme}>
        <div className={styles.loginPage}>
          <div className={styles.loginCard}>
            <img src={androidChromeIcon} alt="Pace AI Logo" className={styles.loginLogo} />
            <Text as="h1" size={800} weight="bold" className={styles.loginTitle}>
              Pace Chat
            </Text>
            <Text size={400} className={styles.loginSubtitle}>
              AI-powered assistant for Pace Applied Solutions
            </Text>
            <Button
              appearance="primary"
              size="large"
              onClick={handleLogin}
              disabled={inProgress !== InteractionStatus.None}
              className={styles.loginButton}
            >
              {inProgress !== InteractionStatus.None ? <Spinner size="tiny" /> : "Sign in with Microsoft"}
            </Button>
          </div>
        </div>
      </FluentProvider>
    );
  }

  const activeAgent = agents.find(a => a.id === activeAgentId) || null;

  return (
    <FluentProvider theme={theme}>
      <div className={styles.app}>
        <HeaderBar
          account={account}
          profilePhoto={profilePhoto}
          isMobileView={isMobileView}
          onToggleSidebar={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
          isDarkMode={isDarkMode}
          onToggleTheme={toggleTheme}
          selectedModel={selectedModel}
          activeAgentName={activeAgent?.name}
          onOpenSettings={() => setIsSettingsOpen(true)}
          ref={headerRef}
        />
        <div className={styles.mainContent} style={{ paddingTop: headerHeight }}>
          <Sidebar
            activeAgentId={activeAgentId}
            account={account}
            agents={agents}
            setAgents={setAgents}
            fetchAgents={fetchAgents}
            isLoadingAgents={isLoadingAgents}
            agentsError={agentsError}
            handleSelectAgent={handleSelectAgent}
            isMobileView={isMobileView}
            isMobileSidebarOpen={isMobileSidebarOpen}
            headerHeight={headerHeight}
            selectedModel={selectedModel}
            conversations={conversations}
            activeConversationId={activeConversationId}
            onSelectConversation={loadConversation}
            onDeleteConversation={handleDeleteConversation}
            onNewConversation={clearConversation}
          />
          <ChatWindow
            activeAgentId={activeAgentId}
            activeAgent={activeAgent}
            messages={messages}
            isLoadingMessages={false}
            isSendingMessage={isSendingMessage}
            chatError={chatError}
            sendMessage={sendMessage}
            stopGeneration={stopGeneration}
            clearConversation={clearConversation}
            regenerateLastResponse={regenerateLastResponse}
            editAndResend={editAndResend}
            availableModels={availableModels}
            selectedModel={selectedModel}
            onModelChange={handleModelChange}
            isLoadingModels={isLoadingModels}
            sessionTokens={sessionTokens}
            userName={account?.name || account?.username}
            profilePhoto={profilePhoto}
            streamingSpeed={streamingSpeed}
          />
        </div>
        <SettingsPanel isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
      </div>
    </FluentProvider>
  );
};

export default App;
